/**
 * ResumeScanner — Upload a resume file (PDF, multi-page) → AI extracts
 * structured profile → review/edit → Add to Candidate Pool.
 * Matching against job openings happens separately on the Job Matches page.
 */
import React, { useState, useRef } from 'react';
import { ScanLine, Loader2, CheckCircle2, Upload, FileText, X, ArrowRight } from 'lucide-react';
import { apiUpload, apiPost } from '../../../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const FIELD = ({ label, children }) => (
  <div className="space-y-1">
    <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</label>
    {children}
  </div>
);

const Input = (props) => (
  <input {...props} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#1F7A6E]" />
);

export default function ResumeScanner() {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);   // raw scan response
  const [edited, setEdited] = useState(null);   // editable fields
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);
  const nav = useNavigate();

  const handleFile = (f) => {
    if (!f) return;
    const ok = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!ok.some(t => f.type === t) && !f.name.match(/\.(pdf|jpg|jpeg|png|webp|doc|docx)$/i)) {
      toast.error('Please upload a PDF, Word document, or image file');
      return;
    }
    setFile(f);
    setResult(null);
    setEdited(null);
  };

  const scan = async () => {
    if (!file) return toast.error('Choose a resume file first');
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('resume', file);
      const r = await apiUpload('/hr/recruitment/scan-resume', fd);
      const ext = r.extracted || {};
      setResult(r);
      // Map Claude field names → pool endpoint field names
      setEdited({
        name: ext.name || '',
        email: ext.email || '',
        phone: ext.phone || '',
        location: ext.location || '',
        current_employer: ext.current_company || '',
        current_role: ext.current_designation || '',
        current_ctc: ext.current_ctc || '',
        expected_ctc: ext.expected_ctc || '',
        notice_period_days: ext.notice_period_days || '',
        years_experience: ext.total_experience_years || '',
        skills: Array.isArray(ext.skills) ? ext.skills.join(', ') : (ext.skills || ''),
        role_fit_summary: ext.role_fit_summary || '',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed — check file format');
    } finally {
      setScanning(false);
    }
  };

  const confirmAndAdd = async () => {
    if (!edited?.name?.trim()) return toast.error('Candidate name is required');
    setAdding(true);
    try {
      const payload = {
        name: edited.name,
        email: edited.email,
        phone: edited.phone,
        location: edited.location,
        current_employer: edited.current_employer,
        current_role: edited.current_role,
        years_experience: parseFloat(edited.years_experience) || 0,
        skills: edited.skills,   // comma string — pool endpoint splits it
        expected_ctc: edited.expected_ctc || null,
        resume_storage_path: result?.resume_url || null,
        ai_summary: edited.role_fit_summary,
        source: 'resume_scan',
      };
      const r = await apiPost('/hr/recruitment/pool', payload);
      toast.success(`${edited.name} added to Candidate Pool`);
      nav(`/app/hr/recruitment/pool/${r.candidate?.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save to pool');
    } finally {
      setAdding(false);
    }
  };

  const set = (k) => (e) => setEdited(prev => ({ ...prev, [k]: e.target.value }));

  return (
    <div className="space-y-6" data-testid="resume-scanner">
      {/* Header */}
      <div className="flex items-center gap-2">
        <ScanLine size={18} className="text-[#1F7A6E]" />
        <h2 className="font-display font-bold text-lg">Resume Scanner</h2>
        <span className="text-[10px] bg-violet-100 text-violet-700 font-bold px-2 py-0.5 rounded-full ml-1">AI-Powered</span>
      </div>
      <p className="text-sm text-slate-500">
        Upload a resume (PDF or image, multi-page supported). AI extracts candidate details automatically
        into your Candidate Pool. Job matching happens separately, after the candidate is saved.
      </p>

      {/* Upload area */}
      {!result && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${file ? 'border-[#1F7A6E] bg-slate-50' : 'border-slate-300 hover:border-[#1F7A6E] hover:bg-slate-50'}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
          data-testid="resume-drop-zone"
        >
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={e => handleFile(e.target.files[0])}
            data-testid="resume-file-input"
          />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText size={20} className="text-[#1F7A6E]" />
              <span className="text-sm font-medium text-slate-700">{file.name}</span>
              <button
                onClick={e => { e.stopPropagation(); setFile(null); }}
                className="text-slate-400 hover:text-rose-500"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload size={28} className="mx-auto text-slate-400" />
              <p className="text-sm text-slate-500">Drop a resume here or <span className="text-[#1F7A6E] font-semibold">browse</span></p>
              <p className="text-xs text-slate-400">PDF (multi-page), Word, JPG, PNG — up to 15 MB</p>
            </div>
          )}
        </div>
      )}

      {!result && (
        <button
          onClick={scan}
          disabled={scanning || !file}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-bold text-sm disabled:opacity-40 transition-colors"
          data-testid="resume-scan-btn"
        >
          {scanning ? <Loader2 size={15} className="animate-spin" /> : <ScanLine size={15} />}
          {scanning ? `Scanning${file ? ' "' + file.name + '"' : ''}…` : 'Scan Resume'}
        </button>
      )}

      {/* Results — editable extracted fields */}
      {result && edited && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-5" data-testid="resume-result">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 size={16} />
              <span className="text-sm font-bold">
                Scanned {result.pages_scanned} page{result.pages_scanned !== 1 ? 's' : ''} — review and edit before adding to pool
              </span>
            </div>
            <button
              onClick={() => { setResult(null); setEdited(null); }}
              className="text-xs text-slate-400 hover:text-slate-600 underline"
            >
              Scan another
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FIELD label="Full Name *">
              <Input value={edited.name} onChange={set('name')} placeholder="Full name" data-testid="edit-name" />
            </FIELD>
            <FIELD label="Email">
              <Input value={edited.email} onChange={set('email')} placeholder="email@example.com" data-testid="edit-email" />
            </FIELD>
            <FIELD label="Phone">
              <Input value={edited.phone} onChange={set('phone')} placeholder="+91 XXXXX XXXXX" data-testid="edit-phone" />
            </FIELD>
            <FIELD label="Location">
              <Input value={edited.location} onChange={set('location')} placeholder="City, State" data-testid="edit-location" />
            </FIELD>
            <FIELD label="Current Company">
              <Input value={edited.current_employer} onChange={set('current_employer')} placeholder="Employer name" data-testid="edit-employer" />
            </FIELD>
            <FIELD label="Current Designation">
              <Input value={edited.current_role} onChange={set('current_role')} placeholder="e.g. Site Engineer" data-testid="edit-role" />
            </FIELD>
            <FIELD label="Total Experience (years)">
              <Input type="number" step="0.5" min="0" value={edited.years_experience} onChange={set('years_experience')} placeholder="e.g. 5.5" data-testid="edit-experience" />
            </FIELD>
            <FIELD label="Notice Period (days)">
              <Input type="number" min="0" value={edited.notice_period_days} onChange={set('notice_period_days')} placeholder="e.g. 30" data-testid="edit-notice" />
            </FIELD>
            <FIELD label="Current CTC (₹)">
              <Input type="number" min="0" value={edited.current_ctc} onChange={set('current_ctc')} placeholder="Annual in ₹" data-testid="edit-ctc" />
            </FIELD>
            <FIELD label="Expected CTC (₹)">
              <Input type="number" min="0" value={edited.expected_ctc} onChange={set('expected_ctc')} placeholder="Annual in ₹" data-testid="edit-expected-ctc" />
            </FIELD>
          </div>

          <FIELD label="Skills (comma-separated)">
            <input
              value={edited.skills}
              onChange={set('skills')}
              placeholder="e.g. AutoCAD, Structural Design, MS Project"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#1F7A6E]"
              data-testid="edit-skills"
            />
          </FIELD>

          {edited.role_fit_summary && (
            <div className="bg-violet-50 rounded-sm px-4 py-3 border border-violet-100">
              <p className="text-[10px] uppercase tracking-widest text-violet-500 font-bold mb-1">Role Fit Assessment</p>
              <p className="text-sm text-slate-700">{edited.role_fit_summary}</p>
            </div>
          )}

          {/* Work history display only */}
          {result.extracted?.work_history?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Work History (extracted, read-only)</p>
              <div className="space-y-2">
                {result.extracted.work_history.slice(0, 4).map((w, i) => (
                  <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded-sm px-3 py-2">
                    <span className="font-semibold">{w.role}</span>
                    {w.company ? ` @ ${w.company}` : ''}
                    {(w.from || w.to) ? <span className="text-slate-400 ml-2">{w.from} – {w.to || 'Present'}</span> : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={confirmAndAdd}
              disabled={adding || !edited.name?.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-bold text-sm disabled:opacity-40 transition-colors"
              data-testid="resume-add-to-pool"
            >
              {adding ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              {adding ? 'Adding to Pool…' : 'Confirm & Add to Candidate Pool'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
